import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DISPATCH_EVENT_KEYS, SIGNALING_COMMANDS } from 'src/consumer/constants'
import {
  checkOutRoom,
  logCheckinRequestIgnored,
} from 'src/clinic/components/CheckIn/services'
import { sessionService } from 'src/consumer/components/LiveSessionPage/services/sessionService'
import { addWindowSignalingEventListener } from 'src/live'

/**
 * Hook to handle all Agra signals for the mentor portal
 * 
 * Signal Format:
 * Messages are sent via Agora RTM and parsed as JSON with the following structure:
 * {
 *   "command": "signal-command-name",
 *   "payload": { ... } // Optional payload data
 * }
 * 
 * The signal is dispatched as a CustomEvent with key DISPATCH_EVENT_KEYS.mentorSignalingEvent
 */
export default function useMentorSignals() {
  const navigate = useNavigate()
  const [videoCallModalData, setVideoCallModalData] = useState(null)
  const [checkinRequestModalData, setCheckinRequestModalData] = useState(null)
  const [checkinRequestLoading, setCheckinRequestLoading] = useState(false)

  useEffect(() => {
    const handleSignalingEvent = async (event) => {
      const command = event?.detail?.command
      const payload = event?.detail?.payload

      console.log('MENTOR SIGNALING EVENT', { command, payload })

      switch (command) {
        case SIGNALING_COMMANDS.REFRESH_BOOKINGS:
          // This is handled in DayViewCalendarComponent
          // Signal is passed through for components that listen to it
          break

        case SIGNALING_COMMANDS.USER_CHECKED_OUT:
          await handleUserCheckedOut(payload)
          break

        case SIGNALING_COMMANDS.VIDEO_CALL_STARTED:
          await handleVideoCallStarted(payload)
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST:
          handleCheckinRequest(payload)
          break

        default:
          console.info('Unknown mentor signaling command:', command)
      }
    }

    return addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.mentorSignalingEvent,
      handler: handleSignalingEvent,
    })
  }, [navigate])

  /**
   * Handle user checkout signal
   * Signal Format:
   * {
   *   "command": "mentor.user-checked-out",
   *   "payload": {
   *     "clinicLocationId": "string", // Optional
   *     "clinicBrandId": "string",    // Optional
   *     "chamberRoomId": "string"     // Optional
   *   }
   * }
   */
  const handleUserCheckedOut = async (payload) => {
    try {
      // Get checked in details from localStorage
      const checkedInDetailsStr = localStorage.getItem('checkedInDetails')
      if (checkedInDetailsStr) {
        const checkedInDetails = JSON.parse(checkedInDetailsStr)
        
        // Perform checkout if checked in details exist
        const checkoutPayload = {
          clinicLocationId: payload?.clinicLocationId || checkedInDetails?.clinicLocationId,
          clinicBrandId: payload?.clinicBrandId || checkedInDetails?.clinicBrandId,
          chamberRoomId: payload?.chamberRoomId || checkedInDetails?.chamberRoomId,
        }

        if (checkoutPayload.clinicLocationId && checkoutPayload.clinicBrandId) {
          await checkOutRoom({
            clinicLocationId: checkoutPayload.clinicLocationId,
            clinicBrandId: checkoutPayload.clinicBrandId,
            chamberRoomId: checkoutPayload.chamberRoomId,
          })
        }
      }

      // Show message and redirect
      toast.info('You have been checked out. Redirecting you to your clinic home.')
      
      // Small delay to show the toast message
      setTimeout(() => {
        navigate('/mentor-home/my-clinic-associations', { replace: true })
      }, 1500)
    } catch (error) {
      console.error('Error handling checkout signal:', error)
      toast.error('Error processing checkout. Please try again.')
      // Still redirect even if checkout fails
      setTimeout(() => {
        navigate('/mentor-home/my-clinic-associations', { replace: true })
      }, 1500)
    }
  }

  /**
   * Handle video call started signal
   * Signal Format:
   * {
   *   "command": "mentor.video-call-started",
   *   "payload": {
   *     "bookedEventId": "string",      // Required: ID of the booked event
   *     "bookedEventSlug": "string",     // Optional: Slug of the booked event (alternative to ID)
   *     "status": "string",              // Optional: "starting" | "started" | "patient-joined"
   *     "timestamp": "string"            // Optional: ISO timestamp when signal was sent
   *   }
   * }
   */
  const handleVideoCallStarted = async (payload) => {
    try {
      const { bookedEventId, bookedEventSlug, status, timestamp } = payload || {}

      if (!bookedEventId && !bookedEventSlug) {
        console.error('Video call signal missing bookedEventId or bookedEventSlug')
        return
      }

      // Try to get bookedEventInfo from localStorage/sessionStorage first
      let bookedEventInfo = null
      const storageKeys = [
        'bookedEventInfo',
        'currentBookedEventInfo',
        'sessionBookedEventInfo',
      ]

      for (const key of storageKeys) {
        const stored = localStorage.getItem(key) || sessionStorage.getItem(key)
        if (stored) {
          try {
            const parsed = JSON.parse(stored)
            // Check if this is the same event
            if (
              parsed?.id === bookedEventId ||
              parsed?.slug === bookedEventSlug ||
              parsed?.slug === bookedEventId
            ) {
              bookedEventInfo = parsed
              break
            }
          } catch (e) {
            console.warn(`Failed to parse stored ${key}:`, e)
          }
        }
      }

      // If not found in storage, fetch from API
      if (!bookedEventInfo) {
        try {
          const response = await sessionService.getBookedEventDetails(
            bookedEventId || bookedEventSlug,
            !!bookedEventSlug, // withSlug = true if slug is provided
          )

          if (response.success) {
            bookedEventInfo = response.bookedEvent
          } else {
            console.error('Failed to fetch booked event details:', response.errorData)
            toast.error('Could not load video call details')
            return
          }
        } catch (error) {
          console.error('Error fetching booked event details:', error)
          toast.error('Could not load video call details')
          return
        }
      }

      // Show modal with video call information
      setVideoCallModalData({
        bookedEventInfo,
        status: status || 'started',
        receivedAt: timestamp || new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error handling video call signal:', error)
      toast.error('Error processing video call notification')
    }
  }

  /**
   * Close video call modal
   */
  const closeVideoCallModal = () => {
    setVideoCallModalData(null)
  }

  /**
   * Handle checkin request signal
   * Signal Format:
   * {
   *   "command": "mentor.checkin-request",
   *   "payload": {
   *     "mentorId": "string",        // Required: ID of the mentor requesting access
   *     "mentorName": "string",      // Required: Name of the mentor
   *     "reason": "string",          // Required: Reason for the checkin request
   *     "requestId": "string",       // Required: Unique ID for this checkin request
   *     "clinicLocationId": "string", // Optional: Clinic location ID
   *     "clinicBrandId": "string",   // Optional: Clinic brand ID
   *     "chamberRoomId": "string"    // Optional: Chamber room ID
   *   }
   * }
   */
  const handleCheckinRequest = (payload) => {
    const { mentorId, mentorName, reason, requestId } = payload || {}

    if (!mentorId || !mentorName || !reason || !requestId) {
      console.error('Checkin request signal missing required fields:', payload)
      toast.error('Invalid checkin request signal')
      return
    }

    // Show the modal
    setCheckinRequestModalData({
      mentorId,
      mentorName,
      reason,
      requestId,
      clinicLocationId: payload?.clinicLocationId,
      clinicBrandId: payload?.clinicBrandId,
      chamberRoomId: payload?.chamberRoomId,
    })
  }

  /**
   * Handle checkout action from checkin request modal
   */
  const handleCheckinRequestCheckout = async () => {
    if (!checkinRequestModalData) return

    setCheckinRequestLoading(true)
    try {
      // Get checked in details from localStorage
      const checkedInDetailsStr = localStorage.getItem('checkedInDetails')
      if (!checkedInDetailsStr) {
        toast.error('No active check-in found')
        setCheckinRequestModalData(null)
        setCheckinRequestLoading(false)
        return
      }

      const checkedInDetails = JSON.parse(checkedInDetailsStr)

      // Prepare checkin request data for checkout API
      const checkinRequestData = {
        mentorId: checkinRequestModalData.mentorId,
        reason: checkinRequestModalData.reason,
        requestId: checkinRequestModalData.requestId,
      }

      // Perform checkout with checkin request data
      await checkOutRoom(checkedInDetails, checkinRequestData)

      toast.success('Checked out successfully')
      setCheckinRequestModalData(null)

      // Redirect to clinic associations page
      setTimeout(() => {
        navigate('/mentor-home/my-clinic-associations', { replace: true })
      }, 1000)
    } catch (error) {
      console.error('Error during checkout from checkin request:', error)
      toast.error('Error processing checkout. Please try again.')
    } finally {
      setCheckinRequestLoading(false)
    }
  }

  /**
   * Handle ignore action from checkin request modal
   */
  const handleCheckinRequestIgnore = async () => {
    if (!checkinRequestModalData) return

    setCheckinRequestLoading(true)
    try {
      // Get checked in details or room data from localStorage
      const checkedInDetailsStr = localStorage.getItem('checkedInDetails')
      const peekedInRoomDetailsStr = localStorage.getItem('peekedInRoomDetails')

      let roomData = null

      if (checkedInDetailsStr) {
        const checkedInDetails = JSON.parse(checkedInDetailsStr)
        roomData = {
          clinicLocationId:
            checkinRequestModalData.clinicLocationId || checkedInDetails?.clinicLocationId,
          clinicBrandId: checkinRequestModalData.clinicBrandId || checkedInDetails?.clinicBrandId,
          chamberRoomId: checkinRequestModalData.chamberRoomId || checkedInDetails?.chamberRoomId,
        }
      } else if (peekedInRoomDetailsStr) {
        const peekedInRoomDetails = JSON.parse(peekedInRoomDetailsStr)
        roomData = {
          clinicLocationId:
            checkinRequestModalData.clinicLocationId || peekedInRoomDetails?.clinicLocationId,
          clinicBrandId: checkinRequestModalData.clinicBrandId || peekedInRoomDetails?.clinicBrandId,
          id: checkinRequestModalData.chamberRoomId || peekedInRoomDetails?.id,
        }
      } else {
        // Use payload data if available
        roomData = {
          clinicLocationId: checkinRequestModalData.clinicLocationId,
          clinicBrandId: checkinRequestModalData.clinicBrandId,
          chamberRoomId: checkinRequestModalData.chamberRoomId,
        }
      }

      // Log the ignored checkin request
      const checkinRequestData = {
        mentorId: checkinRequestModalData.mentorId,
        reason: checkinRequestModalData.reason,
        requestId: checkinRequestModalData.requestId,
      }

      await logCheckinRequestIgnored(roomData, checkinRequestData)

      toast.info('Check-in request ignored')
      setCheckinRequestModalData(null)
    } catch (error) {
      console.error('Error logging ignored checkin request:', error)
      toast.error('Error processing ignore request. Please try again.')
    } finally {
      setCheckinRequestLoading(false)
    }
  }

  /**
   * Close checkin request modal
   */
  const closeCheckinRequestModal = () => {
    if (!checkinRequestLoading) {
      setCheckinRequestModalData(null)
    }
  }

  return {
    videoCallModalData,
    closeVideoCallModal,
    checkinRequestModalData,
    checkinRequestLoading,
    closeCheckinRequestModal,
    handleCheckinRequestCheckout,
    handleCheckinRequestIgnore,
  }
}
